import React from "react";
import { Form, InputNumber, Checkbox, Select, Button } from "antd";
import styled from "styled-components";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";

interface Option {
  label: string;
  value: string;
}

interface Villa {
  name: string | null;
  type_de_cuisine: string | null;
  nbr_de_chambres_min: number | null;
  nbr_de_chambres_max: number | null;
  nbr_de_salles_de_bain_min: number | null;
  nbr_de_salles_de_bain_max: number | null;
  balcon_terasse: boolean;
  surface_terrain_min: number | null;
  surface_terrain_max: number | null;
  surface_construite_min: number | null;
  surface_construite_max: number | null;
  sous_sol: boolean;
}

interface Props {
  form: any;
  formData: any;
  setFormData: any;
}

const typeDeProjetOptions: Record<string, Option[]> = {
  villas: [
    { label: "Isolées", value: "isolées" },
    { label: "Jumelées", value: "jumelées" },
    { label: "En bande", value: "en bande" },
  ],
};

const VillasFormList: React.FC<Props> = ({ form, formData, setFormData }) => {
  const handleSurfaceChange = (
    index: number,
    field: string,
    value: number | null
  ) => {
    const currentPrices = form.getFieldValue("villas_prices") || [];
    const villas = form.getFieldValue("villas") || [];
    const currentPrice = currentPrices[index] || {};

    if (index >= currentPrices.length) return;

    const updatedPrices = [...currentPrices];

    if (field === "surface_construite_min") {
      // If unit price is available, update total price
      if (currentPrice.prix_vente_unitaire_min && value) {
        updatedPrices[index] = {
          ...updatedPrices[index],
          prix_vente_min: Math.round(
            currentPrice.prix_vente_unitaire_min * value
          ),
        };
      }
    } else if (field === "surface_construite_max") {
      // If unit price is available, update total price
      if (currentPrice.prix_vente_unitaire_max && value) {
        updatedPrices[index] = {
          ...updatedPrices[index],
          prix_vente_max: Math.round(
            currentPrice.prix_vente_unitaire_max * value
          ),
        };
      }
    }

    form.setFieldsValue({ villas_prices: updatedPrices });
    setFormData((prev: any) => ({
      ...prev,
      villas_prices: updatedPrices,
    }));
  };

  return (
    <>
      <Form.List name="villas">
        {(fields, { add, remove }) => {
          if (fields.length === 0) {
            add({
              name: null,
              type_de_cuisine: null,
              nbr_de_chambres_min: null,
              nbr_de_chambres_max: null,
              nbr_de_salles_de_bain_min: null,
              nbr_de_salles_de_bain_max: null,
              balcon_terasse: false,
              surface_terrain_min: null,
              surface_terrain_max: null,
              surface_construite_min: null,
              surface_construite_max: null,
              sous_sol: false,
            });
          }
          return (
            <>
              <ScrollableTableWrapper>
                <table
                  style={{
                    minWidth: "1400px",
                    width: "100%",
                    borderCollapse: "collapse",
                    tableLayout: "fixed",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          ...headerStyle,
                          width: "50px",
                          borderBottom: "none",
                          borderLeft: "none",
                          borderTop: "none",
                          backgroundColor: "#fff",
                        }}
                      >
                        <Button
                          color="primary"
                          variant="solid"
                          onClick={() => {
                            add({
                              name: null,
                              type_de_cuisine: null,
                              nbr_de_chambres_min: null,
                              nbr_de_chambres_max: null,
                              nbr_de_salles_de_bain_min: null,
                              nbr_de_salles_de_bain_max: null,
                              balcon_terasse: false,
                              surface_terrain_min: null,
                              surface_terrain_max: null,
                              surface_construite_min: null,
                              surface_construite_max: null,
                              sous_sol: false,
                            });
                          }}
                          icon={<PlusOutlined />}
                        />
                      </th>
                      <th style={{ ...headerStyle, borderBottom: "none" }}>
                        Type
                      </th>
                      <th style={{ ...headerStyle, borderBottom: "none" }}>
                        Cuisine
                      </th>
                      <th
                        style={{ ...headerStyle, borderBottom: "none" }}
                        colSpan={2}
                      >
                        Chambres
                      </th>
                      <th
                        style={{ ...headerStyle, borderBottom: "none" }}
                        colSpan={2}
                      >
                        Salles de Bain
                      </th>
                      <th style={{ ...headerStyle, borderBottom: "none" }}>
                        Balcon/Terrasse
                      </th>
                      <th
                        style={{ ...headerStyle, borderBottom: "none" }}
                        colSpan={2}
                      >
                        Surface Terrain (m²)
                      </th>
                      <th
                        style={{ ...headerStyle, borderBottom: "none" }}
                        colSpan={2}
                      >
                        Surface Construite (m²)
                      </th>
                      <th style={{ ...headerStyle, borderBottom: "none" }}>
                        Sous-sol
                      </th>
                    </tr>
                    <tr>
                      <th
                        style={{
                          ...subHeaderStyle,
                          borderTop: "none",
                          backgroundColor: "#fff",
                        }}
                      ></th>
                      <th style={{ ...subHeaderStyle, borderTop: "none" }}></th>
                      <th style={{ ...subHeaderStyle, borderTop: "none" }}></th>
                      <th style={subHeaderStyle}>Min</th>
                      <th style={subHeaderStyle}>Max</th>
                      <th style={subHeaderStyle}>Min</th>
                      <th style={subHeaderStyle}>Max</th>
                      <th style={{ ...subHeaderStyle, borderTop: "none" }}></th>
                      <th style={subHeaderStyle}>Min</th>
                      <th style={subHeaderStyle}>Max</th>
                      <th style={subHeaderStyle}>Min</th>
                      <th style={subHeaderStyle}>Max</th>
                      <th style={{ ...subHeaderStyle, borderTop: "none" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map(({ key, name, ...restField }) => {
                      const currentValue = form.getFieldValue([
                        "villas",
                        name,
                        "name",
                      ]);
                      return (
                        <tr key={key}>
                          <td style={cellStyle}>
                            <Button
                              variant="solid"
                              color="danger"
                              disabled={fields.length === 1}
                              onClick={() => {
                                remove(name);

                                // After removing apartment, update the prices array
                                setTimeout(() => {
                                  const currentApartments =
                                    form.getFieldValue("villas") || [];
                                  const currentPrices =
                                    form.getFieldValue("villas_prices") || [];

                                  // Remove the corresponding price entry
                                  const newPrices = currentPrices.filter(
                                    (_: any, index: number) => index !== name
                                  );

                                  form.setFieldsValue({
                                    villas_prices: newPrices,
                                  });
                                  setFormData((prev: any) => ({
                                    ...prev,
                                    villas_prices: newPrices,
                                  }));
                                }, 0); // Use setTimeout to ensure the apartment is removed first
                              }}
                              icon={<MinusCircleOutlined />}
                            />
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "name"]}
                              rules={[
                                {
                                  required: true,
                                  message: "",
                                },
                              ]}
                            >
                              <Select
                                style={{ width: "100%" }}
                                placeholder="Select type"
                                value={currentValue}
                                options={typeDeProjetOptions.villas.map(
                                  (option) => {
                                    const currentVillas =
                                      form.getFieldValue("villas") || [];
                                    const isDisabled = currentVillas.some(
                                      (v: Villa, idx: number) =>
                                        idx !== name && v.name === option.value
                                    );
                                    return { ...option, disabled: isDisabled };
                                  }
                                )}
                                onChange={(value) => {
                                  if (!currentValue) {
                                    form.setFields([
                                      {
                                        name: ["villas", name, "name"],
                                        value,
                                      },
                                    ]);
                                  }
                                }}
                              />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "type_de_cuisine"]}
                              rules={[
                                {
                                  required: true,
                                  message: "",
                                },
                              ]}
                            >
                              <Select
                                style={{ width: "100%" }}
                                placeholder="Type de cuisine"
                              >
                                <Select.Option value="fermee">
                                  fermée
                                </Select.Option>
                                <Select.Option value="americaine">
                                  américaine
                                </Select.Option>
                              </Select>
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "nbr_de_chambres_min"]}
                              rules={[
                                { required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const max = getFieldValue([
                                      "villas",
                                      name,
                                      "nbr_de_chambres_max",
                                    ]);
                                    if (!value || !max || value <= max) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber style={{ width: "100%" }} />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "nbr_de_chambres_max"]}
                              rules={[
                                { required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const min = getFieldValue([
                                      "villas",
                                      name,
                                      "nbr_de_chambres_min",
                                    ]);
                                    if (!value || !min || value >= min) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber style={{ width: "100%" }} />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "nbr_de_salles_de_bain_min"]}
                              rules={[
                                { required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const max = getFieldValue([
                                      "villas",
                                      name,
                                      "nbr_de_salles_de_bain_max",
                                    ]);
                                    if (!value || !max || value <= max) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber style={{ width: "100%" }} />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "nbr_de_salles_de_bain_max"]}
                              rules={[
                                { required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const min = getFieldValue([
                                      "villas",
                                      name,
                                      "nbr_de_salles_de_bain_min",
                                    ]);
                                    if (!value || !min || value >= min) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber style={{ width: "100%" }} />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "balcon_terasse"]}
                              valuePropName="checked"
                            >
                              <Checkbox />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "surface_terrain_min"]}
                              rules={[
                                { required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const max = getFieldValue([
                                      "villas",
                                      name,
                                      "surface_terrain_max",
                                    ]);
                                    if (!value || !max || value <= max) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber style={{ width: "100%" }} />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "surface_terrain_max"]}
                              rules={[
                                { required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const min = getFieldValue([
                                      "villas",
                                      name,
                                      "surface_terrain_min",
                                    ]);
                                    if (!value || !min || value >= min) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber style={{ width: "100%" }} />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "surface_construite_min"]}
                              rules={[
                                { required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const max = getFieldValue([
                                      "villas",
                                      name,
                                      "surface_construite_max",
                                    ]);
                                    if (!value || !max || value <= max) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber
                                onChange={(value) =>
                                  handleSurfaceChange(
                                    name,
                                    "surface_construite_min",
                                    typeof value === "string"
                                      ? parseFloat(value)
                                      : value
                                  )
                                }
                                style={{ width: "100%" }}
                              />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "surface_construite_max"]}
                              rules={[
                                { required: true, message: "" },
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const min = getFieldValue([
                                      "villas",
                                      name,
                                      "surface_construite_min",
                                    ]);
                                    if (!value || !min || value >= min) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(""));
                                  },
                                }),
                              ]}
                            >
                              <InputNumber
                                onChange={(value) =>
                                  handleSurfaceChange(
                                    name,
                                    "surface_construite_max",
                                    typeof value === "string"
                                      ? parseFloat(value)
                                      : value
                                  )
                                }
                                style={{ width: "100%" }}
                              />
                            </Form.Item>
                          </td>
                          <td style={cellStyle}>
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              {...restField}
                              name={[name, "sous_sol"]}
                              valuePropName="checked"
                            >
                              <Checkbox />
                            </Form.Item>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ScrollableTableWrapper>
            </>
          );
        }}
      </Form.List>
    </>
  );
};

const headerStyle = {
  border: "2px solid #42005a",
  padding: "3px",
  fontSize: "14px",
  fontWeight: "bold",
  color: "#42005a",
  backgroundColor: "#eeedf0",
  width: "150px",
};

const subHeaderStyle = {
  ...headerStyle,
  fontSize: "12px",
  borderLeft: "none",
};

const cellStyle = {
  border: "2px solid #42005a",
  padding: "3px",
  textAlign: "center" as const,
};

const ScrollableTableWrapper = styled.div`
  overflow-x: auto;
  padding-bottom: 8px;
  margin-top: 16px;

  &::-webkit-scrollbar {
    height: 16px;
    background-color: #f5f5f5;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #42005a;
    border-radius: 4px;
    &:hover {
      background-color: #603570;
    }
  }

  &::-webkit-scrollbar-track {
    background-color: #f1f1f1;
    border-radius: 4px;
  }

  scrollbar-width: thin;
  scrollbar-color: #42005a #f1f1f1;
`;

export default VillasFormList;
